import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { SettingStatus } from '@dataTypes/settingStatus.type'
import { logger } from '@utils/safeLogger'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_BILLING', '')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type CreateAdjustmentTypeRequest = {
  status: SettingStatus
  name: string
  modifier: 'Positive' | 'Negative'
  category: 'Adjustment' | 'Collection'
  clinic_id: string
  is_hidden?: boolean
}

export type UpdateAdjustmentTypeRequest = {
  adjustment_type_id: string
  status?: SettingStatus
  name?: string
  modifier?: 'Positive' | 'Negative'
  category?: 'Adjustment' | 'Collection'
  clinic_id?: string
  is_hidden?: boolean
}

export interface AdjustmentType {
  adjustment_type_id: string
  status: SettingStatus
  name: string
  modifier: 'Positive' | 'Negative'
  category: 'Adjustment' | 'Collection'
  clinic_id: string
  is_hidden?: boolean
}

export type AdjustmentTypeQueryFilters = {
  clinic_id?: string
  status?: SettingStatus | SettingStatus[]
  modifier?: 'Positive' | 'Negative' | Array<'Positive' | 'Negative'>
  category?: 'Adjustment' | 'Collection' | Array<'Adjustment' | 'Collection'>
  search?: string
  sort?: string
  limit?: number
  offset?: number
}

export type AdjustmentTypeListResponse = {
  adjustment_types: AdjustmentType[]
  count: number
}

export const getAllAdjustmentTypes = async (
  filters?: AdjustmentTypeQueryFilters,
): Promise<AdjustmentTypeListResponse> => {
  try {
    const response = await axios.get<
      ApiResponse<{ adjustment_types?: AdjustmentType[]; count?: number }>
    >(`${BUSINESS_LAYER_API}/api/v1/adjustment-type`, {
      params: filters,
    })

    return {
      adjustment_types: response.data?.data?.adjustment_types || [],
      count: response.data?.data?.count || 0,
    }
  } catch (e: unknown) {
    logger.error('Error in getAllAdjustmentTypes:', e)
    throw e
  }
}

export const addAdjustmentType = async (
  payload: CreateAdjustmentTypeRequest,
): Promise<AdjustmentType> => {
  try {
    const response = await axios.post<
      ApiResponse<{ adjustment_type?: AdjustmentType }>
    >(`${BUSINESS_LAYER_API}/api/v1/adjustment-type`, payload)

    if (response.data?.data?.adjustment_type) {
      return response.data.data.adjustment_type
    }
    throw new Error('Failed to create adjustment type')
  } catch (e: unknown) {
    logger.error('Error in addAdjustmentType:', e)
    throw e
  }
}

export const updateAdjustmentType = async (
  payload: UpdateAdjustmentTypeRequest,
): Promise<AdjustmentType> => {
  try {
    const response = await axios.put<
      ApiResponse<{ adjustment_type?: AdjustmentType }>
    >(`${BUSINESS_LAYER_API}/api/v1/adjustment-type`, payload)

    if (response.data?.data?.adjustment_type) {
      return response.data.data.adjustment_type
    }
    throw new Error('Failed to update adjustment type')
  } catch (e: unknown) {
    logger.error('Error in updateAdjustmentType:', e)
    throw e
  }
}

export const deleteAdjustmentType = async (id: string): Promise<void> => {
  try {
    await axios.delete<ApiResponse<Record<string, never>>>(
      `${BUSINESS_LAYER_API}/api/v1/adjustment-type/${id}`,
    )
  } catch (e: unknown) {
    logger.error('Error in deleteAdjustmentType:', e)
    throw e
  }
}
